// @ts-nocheck
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api, logout } from '../services/api'
import DeviceIcon from '../components/DeviceIcon'

function getInitials(name: string, email: string) {
  if (name) return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2)
  return email?.[0]?.toUpperCase() || '?'
}

const BellIcon = ({ active, color = '#888' }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M12 3a7 7 0 0 1 7 7v4l2 3H3l2-3v-4a7 7 0 0 1 7-7z" stroke={active ? '#2D7A3A' : color} strokeWidth="1.8" strokeLinejoin="round" fill={active ? '#f0fdf4' : 'none'}/>
    <path d="M10 18a2 2 0 0 0 4 0" stroke={active ? '#2D7A3A' : color} strokeWidth="1.8"/>
    <line x1="12" y1="3" x2="12" y2="1" stroke={active ? '#2D7A3A' : color} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
)

const HistoryIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#888" strokeWidth="1.8"/>
    <polyline points="12 7 12 12 16 14" stroke="#888" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

export default function Dashboard() {
  const [devices, setDevices] = useState([])
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [newDeviceName, setNewDeviceName] = useState('')
  const [claimKey, setClaimKey] = useState('')
  const [addLoading, setAddLoading] = useState(false)
  const [addError, setAddError] = useState('')
  const [createdDevice, setCreatedDevice] = useState<any>(null)
  const [copied, setCopied] = useState(false)
  const [pairingStep, setPairingStep] = useState<'form' | 'key' | 'guide' | 'waiting'>('form')
  const [deviceOnline, setDeviceOnline] = useState(false)
  const [installPrompt, setInstallPrompt] = useState<any>(null)
  const [unreadCount, setUnreadCount] = useState(0)
  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    const handler = (e: any) => { e.preventDefault(); setInstallPrompt(e) }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  useEffect(() => {
    const userData = localStorage.getItem('user')
    if (!localStorage.getItem('token')) { navigate('/login'); return }
    if (userData) setUser(JSON.parse(userData))
    fetchDevices()
    fetchUnread()

    const refreshInterval = setInterval(fetchDevices, 30000)

    const SESSION_TIMEOUT = 30 * 60 * 1000
    let inactivityTimer: ReturnType<typeof setTimeout>
    const resetTimer = () => {
      clearTimeout(inactivityTimer)
      inactivityTimer = setTimeout(() => { logout(); navigate('/login') }, SESSION_TIMEOUT)
    }
    const events = ['mousemove', 'keydown', 'click', 'touchstart']
    events.forEach(e => window.addEventListener(e, resetTimer))
    resetTimer()

    return () => {
      clearInterval(refreshInterval)
      clearTimeout(inactivityTimer)
      events.forEach(e => window.removeEventListener(e, resetTimer))
    }
  }, [navigate])

  const fetchDevices = () => {
    api.get('/devices')
      .then(res => setDevices(res.data.devices))
      .catch(() => navigate('/login'))
      .finally(() => setLoading(false))
  }

  const fetchUnread = () => {
    const lastSeen = parseInt(localStorage.getItem('notif_last_seen') || '0')
    api.get('/notification-history')
      .then(res => {
        const count = (res.data.notifications || []).filter(
          (n: any) => new Date(n.createdAt).getTime() > lastSeen
        ).length
        setUnreadCount(count)
      })
      .catch(() => {})
  }

  const handleClaim = async (e: React.FormEvent) => {
    e.preventDefault()
    setAddError('')
    setAddLoading(true)
    try {
      const key = claimKey.trim().toUpperCase()
      const res = await api.post('/devices/claim', { deviceKey: key, name: newDeviceName || 'Vigilant' })
      setCreatedDevice(res.data.device)
      setPairingStep('waiting')
      fetchDevices()
    } catch (err: any) {
      setAddError(err.response?.data?.error || 'Chyba — skontroluj kód zariadenia.')
    } finally {
      setAddLoading(false)
    }
  }

  const startWaiting = () => {
    setPairingStep('waiting')
    setDeviceOnline(false)
    const poll = setInterval(async () => {
      try {
        const res = await api.get('/devices')
        const found = (res.data.devices || []).find((d: any) => d.id === createdDevice?.id && d.isOnline)
        if (found) {
          setDeviceOnline(true)
          setDevices(res.data.devices)
          clearInterval(poll)
        }
      } catch {}
    }, 4000)
    // Stop polling after 3 minutes
    setTimeout(() => clearInterval(poll), 3 * 60 * 1000)
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(createdDevice.deviceKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setNewDeviceName('')
    setClaimKey('')
    setAddError('')
    setCreatedDevice(null)
    setCopied(false)
    setPairingStep('form')
    setDeviceOnline(false)
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, backgroundColor: '#2D7A3A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                {/* House */}
                <path d="M3 10.5L12 3l9 7.5V21H15v-5h-6v5H3V10.5z" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" fill="rgba(255,255,255,0.2)"/>
                {/* Dot in center of house */}
                <circle cx="12" cy="14" r="1.3" fill="#fff"/>
                {/* Dot above roof right */}
                <circle cx="18.5" cy="5" r="1.3" fill="#fff"/>
              </svg>
            </div>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', margin: 0, lineHeight: 1.2 }}>Vigilant</h2>
              <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
                {!loading && devices.length > 0
                  ? `${devices.filter((d: any) => d.isOnline).length} online · ${devices.length} ${t('dashboard.subtitle').toLowerCase()}`
                  : t('dashboard.subtitle')}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              onClick={() => {
                localStorage.setItem('notif_last_seen', Date.now().toString())
                setUnreadCount(0)
                navigate('/notifications')
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center', borderRadius: 10, position: 'relative' }}
              title={t('notificationHistory.title')}
            >
              <HistoryIcon />
              {unreadCount > 0 && (
                <span style={{ position: 'absolute', top: 4, right: 4, minWidth: 16, height: 16, borderRadius: 8, backgroundColor: '#e74c3c', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1 }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} title={t('profile.title')}>
              <div style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#2D7A3A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff' }}>
                {getInitials(user?.name, user?.email)}
              </div>
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: 16 }}>
        {/* PWA install banner */}
        {installPrompt && (
          <div style={{ backgroundColor: '#f0fdf4', borderRadius: 14, padding: '14px 18px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#2D7A3A' }}>{t('dashboard.installTitle')}</p>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#666' }}>{t('dashboard.installSubtitle')}</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={async () => { installPrompt.prompt(); setInstallPrompt(null) }} style={{ padding: '8px 14px', borderRadius: 10, border: 'none', backgroundColor: '#2D7A3A', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('dashboard.installBtn')}</button>
              <button onClick={() => setInstallPrompt(null)} style={{ padding: '8px 10px', borderRadius: 10, border: 'none', backgroundColor: 'transparent', color: '#aaa', fontSize: 13, cursor: 'pointer' }}>✕</button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ backgroundColor: '#fff', borderRadius: 16, padding: 18, height: 100 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: '#f0f0f0', marginBottom: 10 }} />
                <div style={{ height: 12, backgroundColor: '#f0f0f0', borderRadius: 6, width: '70%', marginBottom: 6 }} />
                <div style={{ height: 10, backgroundColor: '#f5f5f5', borderRadius: 6, width: '50%' }} />
              </div>
            ))}
          </div>
        ) : devices.length === 0 ? (
          <div style={{ textAlign: 'center', paddingTop: 40 }}>
            <div style={{ width: 72, height: 72, borderRadius: 20, backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M12 2L20 6v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" stroke="#2D7A3A" strokeWidth="1.8" strokeLinejoin="round" fill="#f0fdf4"/></svg>
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 600, color: '#1a1a1a', marginBottom: 6 }}>{t('dashboard.noDevices.title')}</h3>
            <p style={{ fontSize: 15, color: '#888', marginBottom: 28 }}>{t('dashboard.noDevices.subtitle')}</p>
            {[1, 2, 3].map(num => (
              <div key={num} style={{ display: 'flex', alignItems: 'center', gap: 14, backgroundColor: '#fff', borderRadius: 14, padding: '14px 18px', marginBottom: 10, textAlign: 'left', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
                <div style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: '#2D7A3A', color: '#fff', fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{num}</div>
                <span style={{ fontSize: 14, color: '#555' }}>{t(`dashboard.step${num}`)}</span>
              </div>
            ))}
            <button style={{ marginTop: 16, padding: '13px 28px', borderRadius: 12, border: 'none', backgroundColor: '#2D7A3A', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' }} onClick={() => setShowAddModal(true)}>
              {t('dashboard.noDevices.button')}
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {devices.map((device: any) => {
              const battery = device.config?.battery
              const showBattery = battery !== undefined
              return (
                <div
                  key={device.id}
                  onClick={() => navigate(`/device/${device.id}`, { state: { device } })}
                  style={{ backgroundColor: '#fff', borderRadius: 18, padding: '16px 14px', cursor: 'pointer', boxShadow: '0 2px 12px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: 10, position: 'relative' }}
                >
                  {/* Status dot */}
                  <div style={{ position: 'absolute', top: 14, right: 14, width: 8, height: 8, borderRadius: 4, backgroundColor: device.isOnline ? '#2D7A3A' : '#d1d5db' }} />

                  {/* Icon */}
                  <div style={{ width: 44, height: 44, borderRadius: 13, backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DeviceIcon deviceType={device.deviceType} size={22} />
                  </div>

                  {/* Name */}
                  <div>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3 }}>{device.name}</p>
                    {device.isShared && (
                      <span style={{ fontSize: 10, color: '#2D7A3A', backgroundColor: '#f0fdf4', padding: '1px 6px', borderRadius: 8, fontWeight: 600, marginTop: 2, display: 'inline-block' }}>
                        {t('dashboard.shared')}
                      </span>
                    )}
                  </div>

                  {/* Status + battery */}
                  <div>
                    <p style={{ margin: 0, fontSize: 12, color: device.isOnline ? '#2D7A3A' : '#9ca3af', fontWeight: 600 }}>
                      {device.isOnline ? t('dashboard.online') : t('dashboard.offline')}
                    </p>
                    {showBattery && (
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: battery < 20 ? '#ef4444' : '#9ca3af' }}>
                        {t('dashboard.battery')} {battery}%
                      </p>
                    )}
                    {!showBattery && device.lastSeen && (
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9ca3af' }}>
                        {new Date(device.lastSeen).toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => setShowAddModal(true)}
        style={{ position: 'fixed', bottom: 28, right: 24, width: 56, height: 56, borderRadius: 28, backgroundColor: '#2D7A3A', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', boxShadow: '0 4px 16px rgba(45,122,58,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
        title={t('dashboard.addDevice.title')}
      >+</button>

      {/* Add device modal */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 100 }} onClick={e => { if (e.target === e.currentTarget) handleCloseModal() }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '24px 24px 0 0', padding: '28px 28px 36px', width: '100%', maxWidth: 600 }}>

            {/* Step 1: Návod */}
            {pairingStep === 'form' && (
              <>
                <h3 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px', color: '#1a1a1a' }}>{t('dashboard.addDevice.title')}</h3>
                <p style={{ fontSize: 14, color: '#888', margin: '0 0 20px', lineHeight: 1.5 }}>
                  {t('dashboard.addDevice.guide.subtitle')}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 22 }}>
                  {[
                    { n: '1', text: t('dashboard.addDevice.guide.step1') },
                    { n: '2', text: t('dashboard.addDevice.guide.step2') },
                    { n: '3', text: t('dashboard.addDevice.guide.step3') },
                    { n: '4', text: t('dashboard.addDevice.guide.step4') },
                  ].map((item, i, arr) => (
                    <div key={i} style={{ display: 'flex', gap: 14 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: '#f0fdf4', border: '2px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#2D7A3A', flexShrink: 0 }}>{item.n}</div>
                        {i < arr.length - 1 && <div style={{ width: 2, height: 16, backgroundColor: '#e5e7eb', margin: '2px 0' }} />}
                      </div>
                      <div style={{ paddingTop: 5, paddingBottom: i < arr.length - 1 ? 14 : 0 }}>
                        <p style={{ fontSize: 14, color: '#374151', margin: 0, lineHeight: 1.4 }}>{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', backgroundColor: '#2D7A3A', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                  onClick={startWaiting}
                >
                  {t('dashboard.addDevice.guide.start')}
                </button>
                <button style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', backgroundColor: '#f0f0f0', color: '#666', fontSize: 15, cursor: 'pointer', fontFamily: 'inherit', marginTop: 8 }} type="button" onClick={handleCloseModal}>
                  {t('dashboard.addDevice.cancel')}
                </button>
              </>
            )}

            {/* Step 4: Waiting for device */}
            {pairingStep === 'waiting' && (
              <>
                {!deviceOnline ? (
                  <div style={{ textAlign: 'center', padding: '12px 0' }}>
                    <div style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2D7A3A" strokeWidth="2" strokeLinecap="round">
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                      </svg>
                    </div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 8px', color: '#1a1a1a' }}>{t('dashboard.addDevice.waiting.title')}</h3>
                    <p style={{ fontSize: 14, color: '#888', margin: '0 0 24px', lineHeight: 1.6 }}>{t('dashboard.addDevice.waiting.desc')}</p>
                    <div style={{ display: 'flex', gap: 3, justifyContent: 'center', marginBottom: 28 }}>
                      {[0, 1, 2].map(i => (
                        <div key={i} style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#2D7A3A', opacity: 0.3 + i * 0.35,
                          animation: `pulse 1.2s ${i * 0.4}s infinite` }} />
                      ))}
                    </div>
                    <button
                      style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', backgroundColor: '#f0f0f0', color: '#666', fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}
                      onClick={handleCloseModal}
                    >
                      {t('dashboard.addDevice.waiting.skip')}
                    </button>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '12px 0' }}>
                    <div style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#2D7A3A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                    <h3 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px', color: '#1a1a1a' }}>{t('dashboard.addDevice.waiting.success')}</h3>
                    <p style={{ fontSize: 14, color: '#888', margin: '0 0 24px' }}>{t('dashboard.addDevice.waiting.successDesc')}</p>
                    <button
                      style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', backgroundColor: '#2D7A3A', color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                      onClick={() => { handleCloseModal(); navigate(`/device/${createdDevice.id}`) }}
                    >
                      {t('dashboard.addDevice.waiting.done')}
                    </button>
                  </div>
                )}
              </>
            )}

          </div>
        </div>
      )}
    </div>
  )
}
